/**
 * Alpine.js Report Modal Component
 *
 * Global modal for reporting content (users, photos, messages, posts, etc.).
 * Triggered by window.openReportModal(entityType, targetId, targetUid).
 *
 * Usage:
 *   window.openReportModal('user', userId, userId)
 *   window.openReportModal('photo', photoId, photoOwnerId)
 *   window.openReportModal('message', messageId, senderId)
 *   window.openReportModal('post', postId, posterUid)
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

export function reportModal() {
    return {
        isOpen: false,
        entityType: '',
        targetId: '',
        targetUid: '',
        reasonSel: '',
        reason: '',
        isSubmitting: false,
        step: 'select',
        _titles: {},

        init() {
            const titlesEl = this.$el.querySelector('script[data-report-titles]');
            this._titles = titlesEl ? JSON.parse(titlesEl.textContent) : {};
            window.openReportModal = (entityType, targetId, targetUid) => {
                this.open(entityType, targetId, targetUid);
            };
        },

        open(entityType, targetId, targetUid) {
            this.entityType = entityType;
            this.targetId = String(targetId);
            this.targetUid = String(targetUid || targetId);
            this.reasonSel = '';
            this.reason = '';
            this.step = 'select';
            this.isSubmitting = false;
            this.isOpen = true;
            window._reportModalOpen = true;
            const titleEl = document.getElementById('report-modal-title');
            if (titleEl) titleEl.textContent = this._titles[entityType] || this._titles['user'] || '';
            lockBodyScroll();
        },

        close() {
            this.isOpen = false;
            window._reportModalOpen = false;
            unlockBodyScroll();
        },

        selectReason(reason) {
            this.reasonSel = reason;
            this.step = 'detail';
        },

        async submit() {
            if (this.isSubmitting || !this.reasonSel) return;
            this.isSubmitting = true;

            try {
                const token = await window.getApiToken();
                if (!token) throw new Error(window.t ? window.t('common.auth_required') : 'Authentication required');

                const body = new URLSearchParams({
                    entity_type: this.entityType,
                    target_id: this.targetId,
                    target_uid: this.targetUid,
                    reason_sel: this.reasonSel,
                });
                if (this.reason.trim()) {
                    body.set('reason', this.reason.trim());
                }

                const response = await fetch('/v1/report', {
                    method: 'POST',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    credentials: 'include',
                    body,
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));

                    // The reporter already reported this entity inside the
                    // re-report cooldown. Nothing is wrong on their side, so
                    // dismiss the modal and reassure rather than showing an
                    // error they cannot act on.
                    if (data.error?.code === 'report_duplicate') {
                        this.close();
                        window.showSuccess?.(data.error.message
                            || (window.t ? window.t('legacy.report.recent_duplicate') : 'You already reported this.'));
                        return;
                    }

                    throw new Error(data.error?.message || (window.t ? window.t('common.request_failed') : 'Request failed'));
                }

                this.close();
                window.showSuccess?.(window.t ? window.t('legacy.report.submitted') : 'Report submitted. Thank you for helping keep InterPals safe.');
            } catch (error) {
                window.showError?.(error.message || (window.t ? window.t('legacy.report.submit_failed') : 'Failed to submit report'));
            } finally {
                this.isSubmitting = false;
            }
        },
    };
}

if (typeof window !== 'undefined') {
    window.reportModal = reportModal;
}

export default reportModal;
